import { Tooltip } from 'antd';

import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import { TrendArrowUp } from 'assets/svg/TrendArrowUp';
import { colorConstants } from 'config/colorConstants';

import { EMPTY_CELL_VALUE, findCellKpi, hasCellDataForKpis, isTablePriceKpi } from './packArchitectureTable.helpers';
import type { CellData } from './packArchitectureTable.helpers';
import {
    KpiCellRow,
    KpiCellStackContainer,
} from './PackArchitectureTable.styles';

const GrowthIndicator = ({ growthPct }: { growthPct: number }) => {
    const isUp = growthPct > 0;
    const isDown = growthPct < 0;
    const color = isUp
        ? colorConstants.growthUp
        : isDown
          ? colorConstants.growthDown
          : colorConstants.greyAlt14;
    const displayPct = `${Math.abs(growthPct).toFixed(1)}%`;

    return (
        <span className="inline-flex shrink-0 items-center gap-0.5 text-xs leading-none tabular-nums" style={{ color }}>
            {isUp ? <TrendArrowUp className="shrink-0" color={color} /> : null}
            {isDown ? <TrendArrowDown className="shrink-0" color={color} /> : null}
            <span>{displayPct}</span>
        </span>
    );
};

type KpiCellLineProps = {
    cell: CellData | undefined;
    kpiId: string;
    yAxisKpiId: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

const EmptyKpiValue = () => (
    <span className="text-xs" style={{ color: colorConstants.greyAlt14 }}>
        {EMPTY_CELL_VALUE}
    </span>
);

const KpiCellLine = ({ cell, kpiId, yAxisKpiId, matchesKpiId }: KpiCellLineProps) => {
    const entry = findCellKpi(cell ?? {}, kpiId, matchesKpiId);
    const isPrimaryKpi = isTablePriceKpi(kpiId, yAxisKpiId);

    if (!entry || entry.formatted === EMPTY_CELL_VALUE) {
        return <EmptyKpiValue />;
    }

    if (isPrimaryKpi) {
        return (
            <Tooltip title={entry.formatted}>
                <span className="text-sm font-normal leading-5 text-blackTitle">{entry.formatted}</span>
            </Tooltip>
        );
    }

    return (
        <div className="flex w-full items-center justify-between gap-2">
            <Tooltip title={entry.formatted}>
                <span className="truncate text-sm font-normal leading-5 text-blackTitle">{entry.formatted}</span>
            </Tooltip>
            {entry.growthPct != null && Number.isFinite(entry.growthPct) ? (
                <GrowthIndicator growthPct={entry.growthPct} />
            ) : (
                <span className="shrink-0 text-xs tabular-nums" style={{ color: colorConstants.greyAlt14 }}>
                    {EMPTY_CELL_VALUE}
                </span>
            )}
        </div>
    );
};

type KpiCellStackProps = {
    cell: CellData | undefined;
    selectedKpis: string[];
    yAxisKpiId: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
};

export const KpiCellStack = ({ cell, selectedKpis, yAxisKpiId, matchesKpiId }: KpiCellStackProps) => {
    if (!hasCellDataForKpis(cell, selectedKpis, matchesKpiId)) {
        return (
            <KpiCellStackContainer>
                <KpiCellRow $centered>
                    <EmptyKpiValue />
                </KpiCellRow>
            </KpiCellStackContainer>
        );
    }

    return (
        <KpiCellStackContainer>
            {selectedKpis.map((kpiId, index) => (
                <KpiCellRow key={kpiId} $showDivider={index < selectedKpis.length - 1}>
                    <KpiCellLine cell={cell} kpiId={kpiId} yAxisKpiId={yAxisKpiId} matchesKpiId={matchesKpiId} />
                </KpiCellRow>
            ))}
        </KpiCellStackContainer>
    );
};

export { hasCellDataForKpis };
